import { Suspense, lazy } from 'react';
import { RouteObject } from 'react-router';
import { Navigate } from 'react-router-dom';

import BaseLayout from 'src/layouts/BaseLayout';
import SidebarLayout from 'src/layouts/SidebarLayout';
import SuspenseLoader from 'src/components/SuspenseLoader';
import PrivateRoute from 'src/components/PrivateRoute';
import PermissionRoute from 'src/components/PermissionRoute';
import SummaryDetailMobile from './content/dashboards/Summary/DetailMobile';

const Loader = (Component: any) => (props: any) => (
  <Suspense fallback={<SuspenseLoader />}>
    <Component {...props} />
  </Suspense>
);

// Pages
const Login = Loader(lazy(() => import('src/content/auth')));
const IframeViewer = Loader(lazy(() => import('src/content/pages/IframeViewer')));

// Dashboards
const Summary = Loader(lazy(() => import('src/content/dashboards/Summary')));
const Welcome = Loader(lazy(() => import('src/content/welcome')));

// Applications
// const TransactionsOld = Loader(
//   lazy(() => import('src/content/management/Tracking'))
// );
const FreightCalculator = Loader(
  lazy(() => import('src/content/management/FreightCalculator'))
);

// Admin
const UserManagement = Loader(
  lazy(() => import('src/content/admin/user'))
);
const RoleManagement = Loader(
  lazy(() => import('src/content/admin/role'))
);
const MenuManagement = Loader(
  lazy(() => import('src/content/admin/menu'))
);
const CustomerManagement = Loader(
  lazy(() => import('src/content/admin/customer'))
);
const ModuleCardManagement = Loader(
  lazy(() => import('src/content/admin/module-card'))
);
const AdvertisementManagement = Loader(
  lazy(() => import('src/content/admin/advertisement'))
);
const TrackingCardOrderManagement = Loader(
  lazy(() => import('src/content/admin/tracking-card'))
);

// Components
const Buttons = Loader(
  lazy(() => import('src/content/pages/Components/Buttons'))
);
const Modals = Loader(
  lazy(() => import('src/content/pages/Components/Modals'))
);
const Accordions = Loader(
  lazy(() => import('src/content/pages/Components/Accordions'))
);
const Tabs = Loader(lazy(() => import('src/content/pages/Components/Tabs')));
const Badges = Loader(
  lazy(() => import('src/content/pages/Components/Badges'))
);
const Tooltips = Loader(
  lazy(() => import('src/content/pages/Components/Tooltips'))
);
const Avatars = Loader(
  lazy(() => import('src/content/pages/Components/Avatars'))
);
const Cards = Loader(lazy(() => import('src/content/pages/Components/Cards')));
const Forms = Loader(lazy(() => import('src/content/pages/Components/Forms')));

// Status
const Status404 = Loader(
  lazy(() => import('src/content/pages/Status/Status404'))
);
const Status500 = Loader(
  lazy(() => import('src/content/pages/Status/Status500'))
);
const StatusComingSoon = Loader(
  lazy(() => import('src/content/pages/Status/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazy(() => import('src/content/pages/Status/Maintenance'))
);

const routes: RouteObject[] = [
  {
    path: '',
    element: <BaseLayout />,
    children: [
      {
        path: '/',
        element: <Login />
      },
      {
        path: '/register',
        element: <Login />
      },
      {
        path: 'overview',
        element: <Navigate to="/" replace />
      },
      {
        path: 'status',
        children: [
          {
            path: '',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'maintenance',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          }
        ]
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  },
  {
    path: 'welcome',
    element: <PrivateRoute><SidebarLayout /></PrivateRoute>,
    children: [{ path: '', element: <Welcome /> }]
  },
  {
    path: 'dashboards',
    element: <PrivateRoute><SidebarLayout /></PrivateRoute>,
    children: [
      {
        path: '',
        element: <Navigate to="summary" replace />
      },
      {
        path: 'summary',
        element: <Summary />
      },
      {
        path: 'summary/detail/:id',
        element: <SummaryDetailMobile isLoading={false} />
      }
    ]
  },
  {
    path: 'management',
    element: <PrivateRoute><SidebarLayout /></PrivateRoute>,
    children: [
      {
        path: '',
        element: <Navigate to="transactions" replace />
      },
      {
        path: 'transactions',
        element: <Summary />
      },
      // {
      //   path: 'transactions_old',
      //   element: <TransactionsOld />
      // },
      // {
      //   path: 'transactions-old',
      //   element: <TransactionsOld />
      // },
      {
        path: 'freight-calculator',
        element: <FreightCalculator />
      }
    ]
  },
  {
    path: 'admin',
    element: <PrivateRoute><SidebarLayout /></PrivateRoute>,
    children: [
      {
        path: '',
        element: <Navigate to="users" replace />
      },
      {
        path: 'users',
        element: <PermissionRoute permission="User Management"><UserManagement /></PermissionRoute>
      },
      {
        path: 'roles',
        element: <PermissionRoute permission="Role Management"><RoleManagement /></PermissionRoute>
      },
      {
        path: 'menus',
        element: <PermissionRoute permission="Menu Management"><MenuManagement /></PermissionRoute>
      },
      {
        path: 'customers',
        element: <PermissionRoute permission="Customer Management"><CustomerManagement /></PermissionRoute>
      },
      {
        path: 'module-cards',
        element: <PermissionRoute permission="Module Card Management"><ModuleCardManagement /></PermissionRoute>
      },
      {
        path: 'advertisements',
        element: <PermissionRoute permission="Advertisement Management"><AdvertisementManagement /></PermissionRoute>
      },
      {
        path: 'tracking-card-order',
        element: <PermissionRoute permission="Tracking Card Order"><TrackingCardOrderManagement /></PermissionRoute>
      },
      {
        path: 'tracking-cards',
        element: <PermissionRoute permission="Tracking Card Order"><TrackingCardOrderManagement /></PermissionRoute>
      }
    ]
  },
  {
    path: 'external',
    element: <PrivateRoute><SidebarLayout /></PrivateRoute>,
    children: [
      {
        path: ':menuName',
        element: <IframeViewer />
      }
    ]
  },
  {
    path: '/components',
    element: <PrivateRoute><SidebarLayout /></PrivateRoute>,
    children: [
      {
        path: '',
        element: <Navigate to="buttons" replace />
      },
      {
        path: 'buttons',
        element: <Buttons />
      },
      {
        path: 'modals',
        element: <Modals />
      },
      {
        path: 'accordions',
        element: <Accordions />
      },
      {
        path: 'tabs',
        element: <Tabs />
      },
      {
        path: 'badges',
        element: <Badges />
      },
      {
        path: 'tooltips',
        element: <Tooltips />
      },
      {
        path: 'avatars',
        element: <Avatars />
      },
      {
        path: 'cards',
        element: <Cards />
      },
      {
        path: 'forms',
        element: <Forms />
      }
    ]
  }
];

export default routes;
